import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

export const Register = () => {
  const backgroundLogos = useMemo(() => {
    const logos = [];
    const rows = 7;
    const cols = 7;
    const centerRow = 3;
    const centerCol = 3;
    
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const distanceFromCenter = Math.sqrt(
          Math.pow(row - centerRow, 2) + Math.pow(col - centerCol, 2)
        );
        
        // Merkezden uzaklaştıkça küçül (derinlik efekti)
        const sizeBase = 150 - (distanceFromCenter * 18);
        const size = Math.max(70, Math.min(150, sizeBase));
        
        // Merkezden uzaklaştıkça opacity artır
        const opacityBase = 0.10 + (distanceFromCenter * 0.015);
        const opacity = Math.min(0.18, opacityBase).toFixed(2);
        
        // Hafif rotasyon varyasyonu
        const rotateVariation = [-15, -10, -5, 0, 5, 10, 15];
        const rotate = rotateVariation[(row + col) % rotateVariation.length];
        
        logos.push({
          id: row * cols + col,
          size: Math.round(size),
          top: (row * 14) + 2,
          left: (col * 14) + 2,
          rotate: rotate,
          opacity: opacity
        });
      }
    }
    return logos;
  }, []);
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'standard'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Şifreler eşleşmiyor');
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır');
      return;
    }

    setIsLoading(true);
    const result = await register(
      formData.email,
      formData.password,
      formData.name,
      formData.role
    );
    setIsLoading(false);
    
    if (result.success) {
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))] px-4 py-8 relative overflow-hidden">
      {/* Background pattern with 49 logos */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {backgroundLogos.map(logo => (
          <img 
            key={logo.id}
            src="https://customer-assets.emergentagent.com/job_independent-erp/artifacts/331mzum7_Fortis_logo_kurumsal_logo_amblem.png"
            className="absolute"
            style={{
              width: `${logo.size}px`,
              height: 'auto',
              top: `${logo.top}%`,
              left: `${logo.left}%`,
              transform: `rotate(${logo.rotate}deg)`,
              opacity: logo.opacity
            }}
            alt=""
          />
        ))}
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="mb-2 flex items-center justify-center" data-testid="register-logo-container">
          <img 
            src="https://customer-assets.emergentagent.com/job_independent-erp/artifacts/prgex85y_18_01_2025_fortis_kurumsal_logolar.svg" 
            alt="FORTIS Logo" 
            className="h-[410px] w-auto"
            data-testid="register-logo"
          />
        </div>

        <Card data-testid="register-card" className="backdrop-blur-sm bg-white/95 shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-3xl font-bold text-center text-[#8B0000]" data-testid="register-title">
              KAYIT OL
            </CardTitle>
            <CardDescription className="text-center font-bold uppercase" data-testid="register-description">
              FORTIS ERP SİSTEMİNE KATILIN
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
              <div className="space-y-2">
                <Label htmlFor="name" data-testid="register-name-label">Ad Soyad</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Ahmet Yılmaz"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  data-testid="register-name-input"
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" data-testid="register-email-label">E-posta</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="ornek@sirket.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  data-testid="register-email-input"
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" data-testid="register-password-label">Şifre</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  required
                  data-testid="register-password-input"
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" data-testid="register-confirm-password-label">Şifre Tekrar</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  required
                  data-testid="register-confirm-password-input"
                  disabled={isLoading}
                />
              </div>

              {error && (
                <div 
                  className="text-sm text-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/10 p-3 rounded-md"
                  role="alert"
                  data-testid="register-error-message"
                >
                  {error}
                </div>
              )}
              
              <Button 
                type="submit" 
                className="w-full"
                data-testid="register-submit-button"
                disabled={isLoading}
              >
                {isLoading ? 'Kayıt yapılıyor...' : 'Kayıt Ol'}
              </Button>
              
              <div className="text-center text-sm">
                <span className="text-[hsl(var(--muted-foreground))]" data-testid="register-login-prompt">
                  Zaten hesabınız var mı?{' '}
                </span>
                <Link 
                  to="/login" 
                  className="text-[hsl(var(--brand-700))] hover:underline font-medium"
                  data-testid="register-login-link"
                >
                  Giriş Yap
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
        
        {/* Footer */}
        <div className="mt-6 text-center space-y-1.5" data-testid="register-footer">
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Yazılımın Tüm Hakları Saklıdır FORTIS OTOMASYON
          </p>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Created by MEHMET ALİ İDİKURT
          </p>
          <p className="text-sm font-semibold text-[hsl(var(--brand-700))]">
            E-CONFIRMATION V.1.0.1002
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
